import { useState, useCallback } from 'react';
import { useLocation } from 'wouter';
import {
  Bell, Filter, CheckCheck, Timer, ShieldAlert, ClipboardCheck,
  Scissors, CreditCard, FileX, Clock, RefreshCw, ChevronDown,
} from 'lucide-react';
import {
  useBenachrichtigungen,
  prio as prioCss,
  typLabel,
  prioSortOrder,
  type BenachrichtigungPrio,
  type Benachrichtigung,
} from '@/hooks/useBenachrichtigungen';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function relTime(iso: string): string {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1)    return 'Gerade eben';
  if (diffMin < 60)   return `vor ${diffMin} Min`;
  if (diffMin < 1440) return `vor ${Math.floor(diffMin / 60)} Std`;
  if (diffMin < 10080) return `vor ${Math.floor(diffMin / 1440)} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function typIcon(typ: string): React.ElementType {
  if (typ.startsWith('verjährung'))      return Timer;
  if (typ.startsWith('stellungnahme'))   return ShieldAlert;
  if (typ.startsWith('qs'))             return ClipboardCheck;
  if (typ === 'kuerzung_eingegangen')   return Scissors;
  if (typ === 'zahlung_teilweise')      return CreditCard;
  return FileX;
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function BnCard({ b, onRead, onSnooze }: {
  b: Benachrichtigung;
  onRead: (id: string) => void;
  onSnooze: (id: string, d: '1h' | '1d' | '1w') => void;
}) {
  const [, setLocation] = useLocation();
  const [showSnooze, setShowSnooze] = useState(false);
  const css = prioCss(b.prio);
  const Icon = typIcon(b.typ);
  const isRead = !!b.gelesen_am;

  return (
    <div
      className={`rounded-xl border border-gray-200 overflow-hidden ${!isRead ? 'bg-white' : 'bg-gray-50'} border-l-4 ${css.border}`}
    >
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => {
          if (!isRead) onRead(b.id);
          if (b.aktion_url) setLocation(b.aktion_url);
        }}
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${css.badge}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${css.badge}`}>
              {typLabel(b.typ)}
            </span>
            {!isRead && (
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${css.dot}`} />
            )}
            <span className="text-[11px] text-gray-400 flex items-center gap-1 ml-auto flex-shrink-0">
              <Clock className="w-3 h-3" />
              {relTime(b.erstellt_am)}
            </span>
          </div>
          <p className={`text-sm mt-1 ${!isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'}`}>
            {b.titel}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{b.body}</p>
          {b.snooze_bis && new Date(b.snooze_bis) > new Date() && (
            <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Snoozed bis {new Date(b.snooze_bis).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
        {!isRead && (
          <button
            onClick={() => onRead(b.id)}
            className="text-[11px] font-medium text-gray-500 hover:text-gray-800 flex items-center gap-1"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Als gelesen markieren
          </button>
        )}
        <div className="relative ml-auto">
          <button
            onClick={() => setShowSnooze(s => !s)}
            className="text-[11px] font-medium text-gray-400 hover:text-gray-700 flex items-center gap-1"
          >
            <Timer className="w-3.5 h-3.5" />
            Snooze
            <ChevronDown className={`w-3 h-3 transition-transform ${showSnooze ? 'rotate-180' : ''}`} />
          </button>
          {showSnooze && (
            <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              {(['1h', '1d', '1w'] as const).map(d => (
                <button
                  key={d}
                  onClick={() => { onSnooze(b.id, d); setShowSnooze(false); }}
                  className="w-full text-left px-4 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {d === '1h' ? '1 Stunde' : d === '1d' ? '1 Tag' : '1 Woche'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PRIOS: { value: BenachrichtigungPrio | 'all'; label: string }[] = [
  { value: 'all',      label: 'Alle Prioritäten' },
  { value: 'critical', label: 'Kritisch' },
  { value: 'high',     label: 'Hoch' },
  { value: 'medium',   label: 'Mittel' },
  { value: 'low',      label: 'Niedrig' },
];

const TYPEN: { value: string; label: string }[] = [
  { value: '',                       label: 'Alle Typen' },
  { value: 'verjährung_kritisch',    label: 'Verjährung (kritisch)' },
  { value: 'verjährung_warn',        label: 'Verjährung (Warnung)' },
  { value: 'stellungnahmefrist_kritisch', label: 'Stellungnahmefrist (kritisch)' },
  { value: 'stellungnahmefrist_warn', label: 'Stellungnahmefrist (Warnung)' },
  { value: 'qs_erforderlich',        label: 'QS ausstehend' },
  { value: 'qs_korrektur',           label: 'QS Korrektur' },
  { value: 'kuerzung_eingegangen',   label: 'Kürzung eingegangen' },
  { value: 'zahlung_teilweise',      label: 'Teilzahlung offen' },
  { value: 'abtretung_fehlt',        label: 'Abtretung fehlt' },
];

export default function BenachrichtigungenPage() {
  const [filterPrio, setFilterPrio] = useState<BenachrichtigungPrio | 'all'>('all');
  const [filterTyp, setFilterTyp] = useState('');
  const [nurUngelesen, setNurUngelesen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const {
    benachrichtigungen,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    snooze,
    reload,
  } = useBenachrichtigungen({
    prio:        filterPrio !== 'all' ? filterPrio : undefined,
    typ:         filterTyp || undefined,
    nurUngelesen,
  });

  const sorted = [...benachrichtigungen].sort(
    (a, b) => prioSortOrder(b.prio) - prioSortOrder(a.prio)
      || new Date(b.erstellt_am).getTime() - new Date(a.erstellt_am).getTime(),
  );

  const runGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.rpc('benachrichtigungen_erzeugen');
      if (error) throw error;
      toast.success(`${data ?? 0} neue Benachrichtigungen erzeugt`);
      reload();
    } catch (err: any) {
      toast.error('Fehler beim Erzeugen: ' + (err?.message ?? err));
    } finally {
      setGenerating(false);
    }
  }, [reload]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#9B1C1C]/10 flex items-center justify-center">
            <Bell className="w-5 h-5 text-[#9B1C1C]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Benachrichtigungen</h1>
            <p className="text-sm text-gray-500">
              {unreadCount > 0 ? `${unreadCount} ungelesen` : 'Alle gelesen'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              Alle gelesen
            </button>
          )}
          <button
            onClick={runGenerate}
            disabled={generating}
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#9B1C1C] hover:bg-[#7f1818] px-3 py-2 rounded-lg transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            Jetzt prüfen
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 flex-wrap bg-white rounded-xl border border-gray-200 px-4 py-3">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <select
          value={filterPrio}
          onChange={e => setFilterPrio(e.target.value as BenachrichtigungPrio | 'all')}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
        >
          {PRIOS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select
          value={filterTyp}
          onChange={e => setFilterTyp(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
        >
          {TYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto">
          <input
            type="checkbox"
            checked={nurUngelesen}
            onChange={e => setNurUngelesen(e.target.checked)}
            className="rounded border-gray-300 text-[#9B1C1C] focus:ring-[#9B1C1C]/20"
          />
          Nur ungelesen
        </label>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="py-20 text-center">
          <Bell className="w-12 h-12 text-gray-200 mx-auto mb-4" />
          <p className="text-base font-semibold text-gray-400">Keine Benachrichtigungen</p>
          <p className="text-sm text-gray-300 mt-1">
            Klicke auf „Jetzt prüfen" um Benachrichtigungen zu erzeugen.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map(b => (
            <BnCard key={b.id} b={b} onRead={markAsRead} onSnooze={snooze} />
          ))}
        </div>
      )}
    </div>
  );
}
